<template>
    <v-row justify="center" align="center">
        <v-col cols="12" sm="12" md="8">
            <v-card>
                <v-card-title>
                    <h2>{{ data.labelText?.updatePageTitle || 'Update Master Data' }}</h2>
                </v-card-title>

                <v-card-text>
                    <!-- Alert Messages -->
                    <alert-message ref="errorAlert" :message="data.errorAlertMessage"></alert-message>
                    <alert-message
                        ref="successAlert"
                        type="success"
                        :message="data.successAlertMessage"
                    ></alert-message>

                    <!-- Loading State -->
                    <v-progress-linear v-if="data.loading" indeterminate color="primary"></v-progress-linear>

                    <!-- Form -->
                    <v-form ref="form" v-model="data.formValid" v-if="!data.loading && data.record && data.config">
                        <v-row>
                            <v-col cols="12">
                                <v-text-field
                                    v-model="data.record[data.config.codeField]"
                                    :label="data.masterDataCommonLabelText.code"
                                    variant="outlined"
                                    density="comfortable"
                                    readonly
                                    disabled
                                ></v-text-field>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12">
                                <v-text-field
                                    v-model="data.formData.name"
                                    :label="data.masterDataCommonLabelText.name"
                                    :rules="data.nameRules"
                                    required
                                    variant="outlined"
                                    density="comfortable"
                                    :disabled="data.submitting"
                                ></v-text-field>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12">
                                <v-chip :color="data.record.isActive ? 'success' : 'error'" class="ma-2">
                                    Status: {{ data.record.isActive ? 'Active' : 'Inactive' }}
                                </v-chip>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    :model-value="formatDateTime(data.record.dateCreateAt)"
                                    :label="data.masterDataCommonLabelText.dateCreateAt"
                                    variant="outlined"
                                    density="comfortable"
                                    readonly
                                    disabled
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    :model-value="formatDateTime(data.record.dateModifyAt)"
                                    :label="data.masterDataCommonLabelText.dateModifyAt"
                                    variant="outlined"
                                    density="comfortable"
                                    readonly
                                    disabled
                                ></v-text-field>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>

                <v-card-actions>
                    <v-btn text @click="onBackClick" :disabled="data.submitting || data.statusOperation.isLoading">
                        Back to List
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        v-if="!data.loading && data.record"
                        :color="data.record.isActive ? 'warning' : 'success'"
                        @click="onStatusToggleClick"
                        :loading="data.statusOperation.isLoading"
                        :disabled="data.submitting || data.statusOperation.isLoading"
                        class="mr-2"
                    >
                        {{
                            data.record.isActive
                                ? data.labelText?.deactivateButtonText
                                : data.labelText?.activateButtonText
                        }}
                    </v-btn>
                    <v-btn
                        color="primary"
                        @click="onUpdateClick"
                        :loading="data.submitting"
                        :disabled="
                            !data.formValid || data.submitting || !data.hasChanges || data.statusOperation.isLoading
                        "
                    >
                        Update
                    </v-btn>
                </v-card-actions>
            </v-card>

            <!-- Status Change Confirmation Dialog -->
            <v-dialog v-model="data.statusOperation.showConfirmDialog" max-width="500">
                <v-card>
                    <v-card-title class="headline">
                        <v-icon :color="data.statusOperation.pendingStatus ? 'success' : 'warning'" class="mr-2">
                            mdi-alert
                        </v-icon>
                        {{
                            data.statusOperation.pendingStatus
                                ? data.labelText?.confirmActivateTitle
                                : data.labelText?.confirmDeactivateTitle
                        }}
                    </v-card-title>
                    <v-card-text>
                        {{
                            data.statusOperation.pendingStatus
                                ? data.labelText?.confirmActivateMessage
                                : data.labelText?.confirmDeactivateMessage
                        }}
                        <br /><br />
                        <strong>{{ data.labelText?.codeLabel }}:</strong>
                        {{ data.record?.[data.config?.codeField] || '' }}
                        <br />
                        <strong>{{ data.labelText?.nameLabel }}:</strong>
                        {{ data.record?.[data.config?.nameField] || '' }} <br /><br />
                        <v-alert v-if="!data.statusOperation.pendingStatus" type="warning" variant="tonal">
                            This will hide this {{ data.config?.displayName?.toLowerCase() || 'record' }} from dropdown
                            lists.
                        </v-alert>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn text @click="onCancelStatusChange" :disabled="data.statusOperation.isLoading">
                            Cancel
                        </v-btn>
                        <v-btn
                            :color="data.statusOperation.pendingStatus ? 'success' : 'warning'"
                            @click="onConfirmStatusChange"
                            :loading="data.statusOperation.isLoading"
                            :disabled="data.statusOperation.isLoading"
                        >
                            Confirm
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <!-- Status Change Error Dialog -->
            <v-dialog v-model="data.statusOperation.showErrorDialog" max-width="600">
                <v-card>
                    <v-card-title class="headline">
                        <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
                        Cannot Deactivate
                    </v-card-title>
                    <v-card-text>
                        <v-alert type="error" variant="tonal" class="mb-3">
                            This {{ data.config?.displayName?.toLowerCase() || 'record' }} cannot be deactivated because
                            it is currently in use.
                        </v-alert>
                        <div class="text-body-1">
                            {{ data.statusOperation.errorMessage }}
                        </div>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="primary" @click="onCloseErrorDialog"> Close </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { defineComponent, reactive, computed } from 'vue';
import { fetchWrapper } from '../../utils/FetchWrapper';
import { PathDefinition } from '../../utils/PathDefinition';
import AlertMessage from '../../components/AlertMessage.vue';
import { MASTER_DATA_CONFIG } from '../../utils/headerHelper/MasterDataHeaderHelper';
import { MasterDataLabelTextHelper } from '../../utils/labelTextHelper/MasterDataLabelTextHelper';
import { TableFormatHelper } from '../../utils/TableFormatHelper';

export default defineComponent({
    name: 'UpdateMasterData',

    components: {
        AlertMessage
    },

    setup() {
        const data = reactive({
            type: '',
            code: '',
            config: null as any,
            labelText: null as any,
            masterDataCommonLabelText: null as any,
            formValid: false,
            loading: true,
            submitting: false,
            errorAlertMessage: '',
            successAlertMessage: '',
            record: null as any,
            originalName: '',
            formData: {
                name: ''
            },
            nameRules: [(v: string) => !!v || 'Name is required'],
            statusOperation: {
                showConfirmDialog: false,
                showErrorDialog: false,
                isLoading: false,
                pendingStatus: null as boolean | null,
                errorMessage: ''
            }
        });

        const hasChanges = computed(() => {
            return data.formData.name !== data.originalName;
        });

        return {
            data,
            hasChanges: hasChanges
        };
    },

    async mounted() {
        // Get type and code from route params
        this.data.type = this.$route.params.type as string;
        this.data.code = this.$route.params.code as string;

        // Validate type
        if (!MASTER_DATA_CONFIG[this.data.type]) {
            this.data.errorAlertMessage = `Invalid master data type: ${this.data.type}`;
            this.$refs.errorAlert?.showAlert();
            return;
        }

        // Initialize config and labels
        this.data.config = MASTER_DATA_CONFIG[this.data.type];
        this.data.labelText = MasterDataLabelTextHelper.getLabelTextForType(this.data.type);
        this.data.masterDataCommonLabelText = MasterDataLabelTextHelper.commonLabels;

        // Load record
        await this.loadRecord();
    },

    methods: {
        async loadRecord() {
            this.data.loading = true;
            this.data.errorAlertMessage = '';

            try {
                const requestUrl = `/api/staff/${this.data.config.apiPath}/getByCode?${this.data.config.codeField}=${this.data.code}`;
                const response = await fetchWrapper.get(requestUrl, '');

                if (response.success) {
                    this.data.record = response.result;
                    this.data.formData.name = this.data.record[this.data.config.nameField];
                    this.data.originalName = this.data.formData.name;
                } else {
                    this.data.errorAlertMessage = response.errors || 'Failed to load record';
                    this.$refs.errorAlert?.showAlert();
                }
            } catch (error) {
                console.error('Error loading master data:', error);
                this.data.errorAlertMessage = 'An unexpected error occurred';
                this.$refs.errorAlert?.showAlert();
            } finally {
                this.data.loading = false;
            }
        },

        async onUpdateClick() {
            // Validate form
            const form = this.$refs.form as any;
            const { valid } = await form.validate();

            if (!valid) {
                return;
            }

            // Check if there are changes
            if (!this.hasChanges) {
                this.data.errorAlertMessage = 'No changes to save';
                this.$refs.errorAlert?.showAlert();
                return;
            }

            this.data.submitting = true;
            this.data.errorAlertMessage = '';
            this.data.successAlertMessage = '';

            try {
                const requestUrl = `/api/staff/${this.data.config.apiPath}/updateName`;
                const requestData = {
                    [this.data.config.codeField]: this.data.code,
                    [this.data.config.nameField]: this.data.formData.name
                };

                const response = await fetchWrapper.post(requestUrl, requestData);

                if (response.success) {
                    const successMessages = MasterDataLabelTextHelper.getSuccessMessages(this.data.type);
                    this.data.successAlertMessage = successMessages.updated;
                    this.$refs.successAlert?.showAlert();

                    // Wait a moment then navigate back
                    setTimeout(async () => {
                        await this.$router.push({ path: PathDefinition.masterData });
                    }, 1500);
                } else {
                    this.data.errorAlertMessage = response.errors || 'Failed to update record';
                    this.$refs.errorAlert?.showAlert();
                }
            } catch (error) {
                console.error('Error updating master data:', error);
                this.data.errorAlertMessage = 'An unexpected error occurred';
                this.$refs.errorAlert?.showAlert();
            } finally {
                this.data.submitting = false;
            }
        },

        async onBackClick() {
            await this.$router.push({ path: PathDefinition.masterData });
        },

        formatDateTime(iso8601Time: string): string {
            return TableFormatHelper.formatDateTime(iso8601Time);
        },

        onStatusToggleClick() {
            // Set pending status (opposite of current)
            this.data.statusOperation.pendingStatus = !this.data.record.isActive;
            this.data.statusOperation.showConfirmDialog = true;
        },

        async onConfirmStatusChange() {
            this.data.statusOperation.isLoading = true;
            this.data.errorAlertMessage = '';
            this.data.successAlertMessage = '';

            try {
                const requestUrl = `/api/staff/${this.data.config.apiPath}/updateStatus`;
                const requestData = {
                    [this.data.config.codeField]: this.data.code,
                    isActive: this.data.statusOperation.pendingStatus
                };

                const response = await fetchWrapper.post(requestUrl, requestData);

                if (response.success) {
                    // Close confirmation dialog
                    this.data.statusOperation.showConfirmDialog = false;

                    // Show success message
                    const successMessages = MasterDataLabelTextHelper.getSuccessMessages(this.data.type);
                    this.data.successAlertMessage = this.data.statusOperation.pendingStatus
                        ? successMessages.activated
                        : successMessages.deactivated;
                    this.$refs.successAlert?.showAlert('success');

                    // Reload the record to get updated data
                    await this.loadRecord();
                } else {
                    // Close confirmation dialog
                    this.data.statusOperation.showConfirmDialog = false;

                    // Extract error message
                    const errorMessage = response.errors || response.message || 'Failed to update status';
                    this.data.statusOperation.errorMessage = errorMessage;

                    // Show error dialog
                    this.data.statusOperation.showErrorDialog = true;
                }
            } catch (error) {
                console.error('Error updating status:', error);

                // Close confirmation dialog
                this.data.statusOperation.showConfirmDialog = false;

                // Show error
                this.data.statusOperation.errorMessage = 'An unexpected error occurred while updating status';
                this.data.statusOperation.showErrorDialog = true;
            } finally {
                this.data.statusOperation.isLoading = false;
                this.data.statusOperation.pendingStatus = null;
            }
        },

        onCancelStatusChange() {
            this.data.statusOperation.showConfirmDialog = false;
            this.data.statusOperation.pendingStatus = null;
        },

        onCloseErrorDialog() {
            this.data.statusOperation.showErrorDialog = false;
            this.data.statusOperation.errorMessage = '';
        }
    }
});
</script>

<style scoped></style>
